/**
 * Fusion — header
 *
 * Sticky behaviour, desktop dropdown/mega menu management and the rotating
 * announcement bar.
 */

/* ==========================================================================
   Header
   ========================================================================== */

// Grace period before a hover-opened panel actually closes. The flyout sits
// a few pixels outside its trigger row (see .header__dropdown-sublist--flyout
// in component-header.css), so moving the pointer from the row to the panel
// crosses a gap that belongs to neither element — without a delay, that gap
// registers as a real mouseleave and the panel is gone before the pointer
// arrives. Cancelled by the matching mouseenter if the pointer gets there
// before the timer fires.
const HOVER_CLOSE_DELAY = 300;

class FusionHeader extends HTMLElement {
  connectedCallback() {
    this.stickyMode = this.dataset.sticky || null;
    this.lastScroll = window.scrollY;
    this.details = Array.from(this.querySelectorAll('[data-nav-details]'));
    // Nested category flyouts inside a plain "Dropdown" panel (see
    // sections/header.liquid) — a category with sub-links reveals them in a
    // side flyout instead of the panel showing everything expanded at once.
    this.subdetails = Array.from(this.querySelectorAll('[data-nav-subdetails]'));
    this.closeTimers = new WeakMap();

    if (this.stickyMode) this.initSticky();
    this.initDropdowns();
    this.initTransparent();
  }

  /* -- Sticky ------------------------------------------------------------ */

  initSticky() {
    const onScroll = window.Fusion.throttle(() => {
      const current = window.scrollY;
      const threshold = this.offsetHeight;

      this.classList.toggle('is-stuck', current > threshold);

      if (this.stickyMode === 'scroll-up') {
        // Never hide the header while a dropdown is open under it
        const menuOpen = this.details.some((d) => d.hasAttribute('open'));
        const scrollingDown = current > this.lastScroll && current > threshold * 2;
        this.classList.toggle('is-hidden', scrollingDown && !menuOpen);
      }

      this.lastScroll = current;
    }, 100);

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -- Transparent over the first section -------------------------------- */

  initTransparent() {
    if (!this.hasAttribute('data-transparent')) return;

    const onScroll = window.Fusion.throttle(() => {
      this.classList.toggle('is-solid', window.scrollY > 40);
    }, 100);

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -- Hover-intent timers ------------------------------------------------
     A pending close is stored per-element so a follow-up mouseenter on that
     same element can cancel it before it fires. */

  scheduleClose(el, run) {
    this.cancelClose(el);
    const timer = window.setTimeout(run, HOVER_CLOSE_DELAY);
    this.closeTimers.set(el, timer);
  }

  cancelClose(el) {
    const timer = this.closeTimers.get(el);
    if (timer) {
      window.clearTimeout(timer);
      this.closeTimers.delete(el);
    }
  }

  /* -- Dropdowns and mega menus ------------------------------------------ */

  initDropdowns() {
    this.details.forEach((detail) => {
      const summary = detail.querySelector('[data-nav-summary]');

      // Open on hover for pointer users, while keeping click/keyboard working
      detail.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover: hover)').matches) {
          this.cancelClose(detail);
          this.openDetail(detail);
        }
      });

      detail.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        this.scheduleClose(detail, () => {
          detail.removeAttribute('open');
          // Reset any category flyout left open inside it, so the panel
          // starts collapsed again next time it opens instead of the
          // previous category's sub-links still showing.
          this.closeSubdetailsWithin(detail);
        });
      });

      // Close siblings so only one panel is ever open
      summary?.addEventListener('click', () => {
        this.details
          .filter((d) => d !== detail)
          .forEach((d) => {
            this.cancelClose(d);
            d.removeAttribute('open');
            this.closeSubdetailsWithin(d);
          });
      });

      detail.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        this.cancelClose(detail);
        detail.removeAttribute('open');
        this.closeSubdetailsWithin(detail);
        summary?.focus();
      });
    });

    // Category flyouts: same hover-to-open behaviour as the top-level nav
    // above, mutually exclusive within their own panel only — opening
    // "Casserole"'s flyout closes "Dinnerware"'s (or any other category's)
    // flyout under the same "Categories" trigger, instead of both staying
    // open and overlapping. Same close-delay treatment as the top-level nav,
    // for the same reason: the flyout sits outside the row's own box.
    this.subdetails.forEach((subdetail) => {
      const summary = subdetail.querySelector('[data-nav-subsummary]');
      const panel = subdetail.closest('.header__dropdown-list');
      const group = this.subdetails.filter((d) => d !== subdetail && d.closest('.header__dropdown-list') === panel);

      subdetail.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        this.cancelClose(subdetail);
        subdetail.setAttribute('open', '');
        group.forEach((d) => {
          this.cancelClose(d);
          d.removeAttribute('open');
        });
      });

      subdetail.addEventListener('mouseleave', () => {
        if (!window.matchMedia('(hover: hover)').matches) return;
        this.scheduleClose(subdetail, () => subdetail.removeAttribute('open'));
      });

      summary?.addEventListener('click', () => {
        group.forEach((d) => {
          this.cancelClose(d);
          d.removeAttribute('open');
        });
      });

      subdetail.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        this.cancelClose(subdetail);
        subdetail.removeAttribute('open');
        summary?.focus();
      });
    });

    // Close any open panel when focus or a click leaves the header
    document.addEventListener('click', (event) => {
      if (this.contains(event.target)) return;
      this.closeAll();
    });

    document.addEventListener('focusin', (event) => {
      if (this.contains(event.target)) return;
      this.closeAll();
    });
  }

  closeSubdetailsWithin(detail) {
    this.subdetails
      .filter((d) => detail.contains(d))
      .forEach((d) => {
        this.cancelClose(d);
        d.removeAttribute('open');
      });
  }

  openDetail(detail) {
    this.details.forEach((d) => {
      if (d === detail) {
        d.setAttribute('open', '');
      } else {
        this.cancelClose(d);
        d.removeAttribute('open');
        this.closeSubdetailsWithin(d);
      }
    });
  }

  closeAll() {
    this.details.forEach((d) => {
      this.cancelClose(d);
      d.removeAttribute('open');
    });
    this.subdetails.forEach((d) => {
      this.cancelClose(d);
      d.removeAttribute('open');
    });
  }
}

if (!customElements.get('fusion-header')) {
  customElements.define('fusion-header', FusionHeader);
}

/* ==========================================================================
   Announcement rotator
   ========================================================================== */

class FusionAnnouncement extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    this.index = 0;

    const prevButton = this.querySelector('[data-announcement-prev]');
    const nextButton = this.querySelector('[data-announcement-next]');
    prevButton?.addEventListener('click', () => this.show(this.index - 1));
    nextButton?.addEventListener('click', () => this.show(this.index + 1));

    if (this.slides.length < 2) return;

    // Autoplay is independent of the arrow buttons above — a merchant can
    // offer either, both, or neither.
    if (this.hasAttribute('data-autorotate') && window.Fusion.motionAllowed()) {
      this.speed = parseInt(this.dataset.speed, 10) || 5000;
      this.start();

      // Pause while the visitor is reading or interacting
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.stop() : this.start();
      });
    }
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.timer = window.setInterval(() => this.next(), this.speed);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  next() {
    this.show(this.index + 1);
  }

  show(index) {
    const total = this.slides.length;
    const previous = this.slides[this.index];
    this.index = ((index % total) + total) % total;
    const current = this.slides[this.index];
    if (current === previous) return;

    previous.classList.add('is-hidden');
    previous.setAttribute('aria-hidden', 'true');

    current.classList.remove('is-hidden');
    current.removeAttribute('aria-hidden');
  }
}

if (!customElements.get('fusion-announcement')) {
  customElements.define('fusion-announcement', FusionAnnouncement);
}

/* ==========================================================================
   Localization forms
   ========================================================================== */

/**
 * Submits the country/language form on change so no extra button is needed.
 * The <noscript> submit button in the markup covers the no-JS case.
 */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-localization-select]');
  if (!select) return;
  select.form?.submit();
});
